import { clsx } from 'clsx';
import { AutoComplete } from 'antd';
import { useController } from 'react-hook-form';
import React, { useEffect, useState } from 'react';

import { http } from '@network';
import { InitialRowProps } from '@components/InitialCard';
import cls from './AutocompleteFilter.module.css';


interface ListOption {
  value: number;
  label: string;
  disabled?: boolean;
}

interface Props {
  source: string;
  reference: string;
  optionKey?: string;
  optionLabel?: string;
  disabledProp?: string;
  style?: any;
  listFieldName?: string;
  label?: string;
}

export const AutocompleteFilter = ({
  label,
  source,
  reference,
  optionKey,
  optionLabel,
  disabledProp,
  listFieldName,
  ...initialRowProps
}: Props & Omit<InitialRowProps, 'title'>) => {
  const {
    fieldState: { error },
    field: { value: id, onChange: setId },
  } = useController({ name: source });

  const [text, setText] = useState('');
  const [allItems, setAllItems] = useState<ListOption[]>([]);
  const [filteredItems, setFilteredItems] = useState<ListOption[]>([]);
  const errorMessage = (error as any)?.message?.message;

  const onSearch = (searchText: string) => {
    setText(searchText);

    setFilteredItems(
      searchText ? allItems.filter(o =>
        o.label.toLowerCase().includes(searchText.toLowerCase()),
      ) : allItems,
    );
  };

  const onSelect = (value: string) => {
    const selected = allItems.find(item => item.value === +value);

    if (selected) {
      setId(selected.value);
      setText(selected.label);
    }
  };

  const onClear = () => {
    setId(null);
    setText('');
  };

  useEffect(() => {
    (async () => {
      const response = await http.get<any>(reference || source, {
        params: { _start: 0, _end: 9999999 },
      });

      const rawData = listFieldName
        ? response.data?.[listFieldName]
        : response.data;

      const nextOptions: ListOption[] = [];

      if (Array.isArray(rawData)) {
        const data = listFieldName ? response.data[listFieldName] : response.data;
        data.forEach(model =>
          nextOptions.push({
            value: +model[optionKey ?? 'id'],
            label:
              model[optionLabel ?? 'nameMx'] ||
              model.name ||
              model.title ||
              model.activation,
            disabled: disabledProp ? !!model[disabledProp] : undefined,
          }),
        );
      }

      nextOptions.sort((a, b) =>
        a.label.toLowerCase().localeCompare(b.label.toLowerCase()),
      );

      setAllItems(nextOptions);
      setFilteredItems(nextOptions);

      if (id) {
        const selected = nextOptions.find(i => i.value === id);
        if (selected) {
          setText(selected.label);
        }
      }
    })();
  }, []);

  return (
    <div className={cls.wrap} style={{ ...initialRowProps.style }}>
      <AutoComplete
        value={text}
        onChange={setText}
        onSearch={onSearch}
        onSelect={onSelect}
        onClear={onClear}
        allowClear
        options={filteredItems.map(item => ({
          value: String(item.value),
          label: item.label,
          disabled: item.disabled,
        }))}
        className={cls.autocompleteField}
        notFoundContent="Not found"
        status={errorMessage ? 'error' : undefined}
      />
      {label ? <span className={clsx(cls.label, text && cls.labelFilled)}>{label}</span> : null}
      <span className={clsx(cls.search, text && cls.searchHide)}/>
    </div>
  );
};
